import React from 'react';
import { css } from '@emotion/react';
import { Controller, Control } from 'react-hook-form';
import {
  TextField,
  FormControl,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import HelpSharpIcon from '@mui/icons-material/HelpSharp';
import { ICreateResidentParams } from '../../../types/api/resident/createResident';
import { IGetUnitRes } from '../../../types/api/unit/getUnit';
import { IGetNursingHomeRes } from '../../../types/api/nursingHome/getNursingHome';
import { IGetDfreeDevicesRes } from '../../../types/api/dfree-devices/getDfreeDevices';
import { IGetDfreeBDevicesRes } from '../../../types/api/dfree-b-devices/getDfreeBDevices';
import { useTranslation } from 'react-i18next';
import { MenuItemProps, Select } from '../../../components/mui/select';
import { color } from '../../../styles';
import { media } from '../../../styles/media';
import {
  BLADDER_THRESHOLD_PERCENTAGE,
  HOURS,
  SNOOZETERM_JA,
  SNOOZETERM_EN,
  SNOOZEPERIOD_JA,
  SNOOZEPERIOD_EN,
  DETAKAMO_LOGIC_JA,
  DETAKAMO_LOGIC_EN,
  MED_SEX_JA,
  MED_SEX_EN,
} from '../../../constants';
import { SUPPORT_URL } from '../../../constants';

interface Props {
  isEdit: boolean;
  control: Control<ICreateResidentParams, object>;
  units: IGetUnitRes[];
  formSelectLists: {
    pants: { key: number | string; value: string }[];
    pads: { key: number | string; value: string }[];
    devices: IGetDfreeDevicesRes[];
    goodTreeList: { key: number | string; value: string }[];
    cdcList: { key: string; value: string }[];
    paramountList: { key: string; value: string }[];
    bDevices: IGetDfreeBDevicesRes[];
    careLevel: { key: number | string; value: string }[];
  };
  deviceId: string;
  bDeviceId: string;
  careSchedule: string[];
  isDisplayBigBladderOption?: boolean;
  isDisplayUrinationOption?: boolean;
  isDisplayGotUpOption?: boolean;
  nameError?: boolean;
  lastNameError?: boolean;
  sexError?: boolean;
  unitError?: boolean;
  endDiaperMiddleHourError?: boolean;
  isAfternoonEndBeforeNightStart: (afternoonEnd: string | null) => boolean;
  isOnlyOneSelectedMiddle: (value: string | null, isBegin: boolean) => boolean;
  isOnlyOneSelectedNight: (value: string | null, isBegin: boolean) => boolean;
  onChangeCareSchedule: (editType: 'add' | 'change' | 'delete', schedule?: string, index?: number) => void;
  onChangeDeviceId: (id: string) => void;
  onChangeBDeviceId: (id: string) => void;
  onChangeSnoozeTerm: (type: string, value: string | number) => void;
  onChangeSnoozePeriod: (type: string, value: string | number) => void;
  onChangeDetakamoLogic: (value: string) => void;
  nursingHome: IGetNursingHomeRes;
  isEmptyFormData: Record<string, boolean>;
  hoursWithUnSelected: { key: string; value: string }[];
  localHoursWithUnSelected: { key: string; value: string }[];
}

const WidthMax = css`
  width: 100%;
`;

const TopSpacing = css`
  margin-top: 16px;
`;

const ScrollMargin = css`
  scroll-margin-top: 112px;
`;

const Container = css`
  padding: 40px 16px 0;
  ${media.minDesktop} {
    padding: 40px 100px 0;
  }
`;

const FormItem = css`
  display: flex;
`;

const AlignCenter = css`
  display: flex;
  align-items: center;
`;

const FlexBetween = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Label = css`
  flex: 0 0 18%;
  margin-right: 8px;
`;

const Required = css`
  color: #eb3223;
`;

const Font16 = css`
  font-size: 16px;
`;

const BottomSpacing = css`
  margin-bottom: 16px;
`;

const BottomLine = css`
  border-bottom: 1px solid ${color.border_gray};
`;

const FormContent = css`
  width: 80%;
  flex: 1 1 80%;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const CheckboxSelectContainer = css`
  padding: 8px 16px;
  background: ${color.light_green};
  border-radius: 8px;
  margin-right: 8px;
  ${BottomSpacing};
`;

const GotUpContainer = css`
  display: flex;
  align-items: center;
  justify-content: start;
  flex-direction: column;
  ${BottomSpacing};
`;

const BarTitle = css`
  padding: 4px 15px;
  border-left: 5px solid;
  font-weight: bold;
`;

const InlineHelp = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
const NotificationImage = css`
  margin-right: 8px;
  width: 24px;
  min-width: 24px;
  height: 24px;
`;

const ContingencyModeSub = css`
  color: ${color.dark_gray};
  font-size: 14px;
`;

const ContingencyModeText = css`
  padding: 12px 16px;
  background: ${color.light_green};
  border-radius: 8px;
  font-size: 16px;
`;

export const MedicalResidentSettingForm = React.memo(
  ({
    isEdit,
    control,
    units,
    formSelectLists,
    deviceId,
    onChangeDeviceId,
    nameError = false,
    lastNameError = false,
    sexError = false,
    unitError = false,
    isEmptyFormData,
    nursingHome,
    isDisplayBigBladderOption = false,
    isDisplayUrinationOption = false,
    isDisplayGotUpOption = false,
    onChangeSnoozeTerm,
    onChangeSnoozePeriod,
    onChangeDetakamoLogic,
  }: Props) => {
    const { t, i18n } = useTranslation();

    const defaultTheme = useTheme();
    const tooltipTheme = React.useMemo(
      () =>
        createTheme({
          ...defaultTheme,
          components: {
            MuiTooltip: {
              defaultProps: { arrow: true },
              styleOverrides: {
                tooltip: {
                  maxWidth: 560,
                  fontSize: '14px',
                  padding: '8px',
                },
                arrow: { color: color.dark_gray },
              },
            },
          },
        }),
      []
    );

    const sexOptions = React.useMemo<MenuItemProps[]>(() => {
      return i18n.language.startsWith('ja') ? MED_SEX_JA : MED_SEX_EN;
    }, [i18n.language]);

    const memoUnits = React.useMemo(() => {
      return units.map((unit) => {
        return { key: unit.id, value: unit.name };
      });
    }, [units]);

    const memoDevices = React.useMemo(() => {
      const devices = formSelectLists.devices.map((device) => {
        let attachedName = '';
        if (device.lastName || device.firstName) {
          const lastName = device.lastName ? device.lastName : '';
          const firstName = device.firstName ? device.firstName : '';
          if (i18n.language.startsWith('ja')) {
            attachedName = ' (' + lastName + ' ' + firstName + 'さん 利用中)';
          } else {
            attachedName = ' (' + firstName + ' ' + lastName + ' In use)';
          }
        }
        return { key: String(device.id), value: device.serialNumber + attachedName };
      });
      devices.unshift({ key: '', value: t('unregisterDFree', 'DFree登録を解除') });
      return devices;
    }, [formSelectLists.devices, i18n.language, t]);

    return (
      <ThemeProvider theme={tooltipTheme}>
        <div css={Container}>
          <div css={[BarTitle, BottomSpacing]}>{t('editResidentInformation_medical', '利用者情報編集')}</div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>
              {t('lastName', '名')}
              <span css={Required}>*</span>
            </label>
            <div css={FormContent}>
              <Controller
                control={control}
                name='lastName'
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    sx={{ width: '100%' }}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    label={t('lastName', '名')}
                    error={lastNameError}
                    helperText={
                      lastNameError &&
                      t('firstName', '姓') +
                        (i18n.language.startsWith('ja') ? '' : ' ') +
                        t('isRequired', 'は必須入力です。')
                    }
                  />
                )}
              />
            </div>
          </div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>
              {t('firstName', '姓')}
              <span css={Required}>*</span>
            </label>
            <div css={FormContent}>
              <Controller
                control={control}
                name='name'
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    sx={{ width: '100%' }}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    error={nameError}
                    label={t('firstName', '姓')}
                    helperText={
                      nameError &&
                      t('lastName', '名') +
                        (i18n.language.startsWith('ja') ? '' : ' ') +
                        t('isRequired', 'は必須入力です。')
                    }
                  />
                )}
              />
            </div>
          </div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>
              {t('gender', '性別')}
              <span css={Required}>*</span>
            </label>
            <div css={FormContent}>
              <Controller
                control={control}
                name='sex'
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    label={t('gender', '性別')}
                    listData={sexOptions}
                    value={value ?? null}
                    isDisplayEmpty
                    isEmptyValue={!value}
                    isError={sexError}
                    errorText={sexError ? t('gender', '性別') + t('isRequired', 'は必須入力です。') : ''}
                    fullWidth
                    onChange={(v) => onChange(v.key === '-' ? null : v.key)}
                  />
                )}
              />
            </div>
          </div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>
              {t('unitName', 'ユニット名')}
              <span css={Required}>*</span>
            </label>
            <div css={FormContent}>
              <Controller
                control={control}
                name='nursingHomeUnitId'
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? String(value) : null}
                    listData={memoUnits}
                    label={t('unitName', 'ユニット名')}
                    onChange={(v) => {
                      onChange(v.key);
                    }}
                    isError={unitError}
                    fullWidth
                    errorText={
                      unitError
                        ? t('unitName', 'ユニット名') +
                          (i18n.language.startsWith('ja') ? '' : ' ') +
                          t('isRequired', 'は必須入力です。')
                        : ''
                    }
                  />
                )}
              />
            </div>
          </div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>{t('roomName', '部屋名')}</label>
            <div css={FormContent}>
              <Controller
                control={control}
                name='roomName'
                render={({ field: { onChange, value } }) => (
                  <TextField sx={{ width: '100%' }} label={t('roomName', '部屋名')} onChange={onChange} value={value} />
                )}
              />
            </div>
          </div>
          <div css={[BarTitle, BottomSpacing]}>{t('dFreeSettings', 'DFree設定')}</div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>{t('dfreeSn', 'DFree SN')}</label>
            <div css={FormContent}>
              <FormControl sx={{ width: '100%' }}>
                <Select
                  label={t('dfreeSn', 'DFree SN')}
                  listData={memoDevices}
                  value={deviceId ?? ''}
                  onChange={(v) => onChangeDeviceId(v.key as string)}
                />
              </FormControl>
            </div>
          </div>
          <Accordion
            disableGutters
            sx={{
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              mb: 2,
            }}
          >
            <AccordionSummary css={BottomLine} expandIcon={<ExpandMoreIcon />} sx={{ px: 0, mb: 2 }}>
              <div css={BarTitle}>{t('notificationSettings', '通知設定')}</div>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <div css={FormItem}>
                <label css={Label}></label>
                <div css={FormContent}>
                  <Controller
                    control={control}
                    name='bigBladderNotification'
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        sx={{ marginBottom: '8px' }}
                        label={t('receiveAlmostNotification', 'そろそろ通知を受け取る')}
                        control={
                          <>
                            <Checkbox
                              sx={{ padding: 0, marginRight: '8px' }}
                              checked={value}
                              onChange={(v) => onChange(v.target.checked)}
                            />
                            <img css={NotificationImage} src={`/images/notification/icon_bigbladder.svg`} />
                          </>
                        }
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name='bladderThresholdPercentage'
                    render={({ field: { onChange, value } }) => (
                      <div css={CheckboxSelectContainer}>
                        <div css={FlexBetween}>
                          <div>{t('almostThreshold', 'そろそろライン')}</div>
                          <Select
                            listData={BLADDER_THRESHOLD_PERCENTAGE}
                            value={String(value)}
                            onChange={(v) => onChange(v.key)}
                          />
                        </div>
                      </div>
                    )}
                  />
                  {isDisplayBigBladderOption && (
                    <>
                      <Controller
                        control={control}
                        name='bigBladderSnoozeTerm'
                        render={({ field: { onChange, value } }) => (
                          <div css={CheckboxSelectContainer}>
                            <div css={FlexBetween}>
                              <div>{t('SnoozeInterval', '繰り返し間隔')}</div>
                              <Select
                                listData={i18n.language.startsWith('ja') ? SNOOZETERM_JA : SNOOZETERM_EN}
                                value={String(value)}
                                onChange={(v) => {
                                  onChange(+v.key);
                                  onChangeSnoozeTerm('bigBladder', v.key);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      />
                      <Controller
                        control={control}
                        name='bigBladderSnoozePeriod'
                        render={({ field: { onChange, value } }) => (
                          <div css={CheckboxSelectContainer}>
                            <div css={FlexBetween}>
                              <div>{t('SnoozeEnd', '繰り返し終了')}</div>
                              <Select
                                listData={i18n.language.startsWith('ja') ? SNOOZEPERIOD_JA : SNOOZEPERIOD_EN}
                                value={String(value)}
                                onChange={(v) => {
                                  onChange(+v.key);
                                  onChangeSnoozePeriod('bigBladder', v.key);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      />
                    </>
                  )}
                  <Controller
                    control={control}
                    name='urinationNotification'
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <FormControlLabel
                          label={
                            <span css={InlineHelp}>
                              {t('receiveCheckNotification', 'でたかも通知を受け取る')}
                              <Tooltip
                                title={t(
                                  'dfreeExplanationForUrinationNotification',
                                  'DFreeは、膀胱の尿のたまり具合を見ている機器です。ご利用者さまの動きや姿勢により、膀胱が見えなくなった場合にも通知されることがあるため、ベッド上で過ごされる方への使用を推奨しております。トイレへ行くことができる方には、「そろそろ通知」のご使用をおすすめいたします。',
                                )}
                                placement='top'
                                arrow
                              >
                                <HelpSharpIcon fontSize='small' sx={{ color: color.dark_gray }} />
                              </Tooltip>
                            </span>
                          }
                          control={
                            <>
                              <Checkbox
                                sx={{ padding: 0, marginRight: '8px' }}
                                checked={value}
                                onChange={(v) => onChange(v.target.checked)}
                              />
                              <img css={NotificationImage} src={`/images/notification/icon_urinated.svg`} />
                            </>
                          }
                        />
                      </div>
                    )}
                  />
                  <br />
                  {isDisplayUrinationOption && (
                    <>
                      <Controller
                        control={control}
                        name='urinationSnoozeTerm'
                        render={({ field: { onChange, value } }) => (
                          <div css={CheckboxSelectContainer}>
                            <div css={FlexBetween}>
                              <div>{t('SnoozeInterval', '繰り返し間隔')}</div>
                              <Select
                                listData={i18n.language.startsWith('ja') ? SNOOZETERM_JA : SNOOZETERM_EN}
                                value={String(value)}
                                onChange={(v) => {
                                  onChange(+v.key);
                                  onChangeSnoozeTerm('urination', v.key);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      />
                      <Controller
                        control={control}
                        name='urinationSnoozePeriod'
                        render={({ field: { onChange, value } }) => (
                          <div css={CheckboxSelectContainer}>
                            <div css={FlexBetween}>
                              <div>{t('SnoozeEnd', '繰り返し終了')}</div>
                              <Select
                                listData={i18n.language.startsWith('ja') ? SNOOZEPERIOD_JA : SNOOZEPERIOD_EN}
                                value={String(value)}
                                onChange={(v) => {
                                  onChange(+v.key);
                                  onChangeSnoozePeriod('urination', v.key);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      />
                    </>
                  )}
                  <Controller
                    control={control}
                    name='gotUpNotification'
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <FormControlLabel
                          sx={{ marginBottom: '8px' }}
                          label={t('receiveGettingUpNotification', '起き上がり通知を受け取る')}
                          control={
                            <>
                              <Checkbox
                                sx={{ padding: 0, marginRight: '8px' }}
                                checked={value}
                                onChange={(v) => onChange(v.target.checked)}
                              />
                              <img css={NotificationImage} src={`/images/notification/icon_gotUp.svg`} />
                            </>
                          }
                        />
                      </div>
                    )}
                  />
                  {isDisplayGotUpOption && (
                    <>
                      <div css={[CheckboxSelectContainer, GotUpContainer]}>
                        <div css={[WidthMax, FlexBetween]}>
                          <div>{t('notificationTimeZone', '通知時間帯')}</div>
                          <div css={AlignCenter}>
                            <Controller
                              control={control}
                              name='beginGotUpCheckTerm'
                              render={({ field: { onChange, value } }) => (
                                <Select listData={HOURS} value={value} onChange={(v) => onChange(v.key)} />
                              )}
                            />
                            -
                            <Controller
                              control={control}
                              name='endGotUpCheckTerm'
                              render={({ field: { onChange, value } }) => (
                                <Select listData={HOURS} value={value} onChange={(v) => onChange(v.key)} />
                              )}
                            />
                          </div>
                        </div>
                        <div css={[WidthMax, ContingencyModeSub]}>
                          {t(
                            'NotificationLoopSettingDescription',
                            '※起き上がり通知を24時間有効にしたい場合は、上欄の開始時刻と終了時刻を同じ時刻に設定してください。例）19:00 - 19:00',
                          )}
                        </div>
                      </div>
                      <Controller
                        control={control}
                        name='gotUpSnoozeTerm'
                        render={({ field: { onChange, value } }) => (
                          <div css={[CheckboxSelectContainer, GotUpContainer]}>
                            <div css={[WidthMax, FlexBetween]}>
                              <div>{t('SnoozeInterval', '繰り返し間隔')}</div>
                              <Select
                                listData={i18n.language.startsWith('ja') ? SNOOZETERM_JA : SNOOZETERM_EN}
                                value={String(value)}
                                onChange={(v) => {
                                  onChange(+v.key);
                                  onChangeSnoozeTerm('gotUp', v.key);
                                }}
                              />
                            </div>
                            <div css={[WidthMax, ContingencyModeSub]}>
                              ※{t('NotificationIntervalMayVaryBySeveralMinutes', '間隔が数分前後することがあります')}
                            </div>
                          </div>
                        )}
                      />
                      <Controller
                        control={control}
                        name='gotUpSnoozePeriod'
                        render={({ field: { onChange, value } }) => (
                          <div css={CheckboxSelectContainer}>
                            <div css={FlexBetween}>
                              <div>{t('SnoozeEnd', '繰り返し終了')}</div>
                              <Select
                                listData={i18n.language.startsWith('ja') ? SNOOZEPERIOD_JA : SNOOZEPERIOD_EN}
                                value={String(value)}
                                onChange={(v) => {
                                  onChange(+v.key);
                                  onChangeSnoozePeriod('gotUp', v.key);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      />
                    </>
                  )}
                  <Controller
                    control={control}
                    name='zeroResetNotification'
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        sx={{ marginBottom: '8px' }}
                        label={t('notificationIfSensorIsNotAttachedProperly', '装着開始時エラー通知を受け取る')}
                        control={
                          <>
                            <Checkbox
                              sx={{ padding: 0, marginRight: '8px' }}
                              checked={value}
                              onChange={(v) => onChange(v.target.checked)}
                            />
                            <img css={NotificationImage} src={`/images/notification/icon_zeroReset.svg`} />
                          </>
                        }
                      />
                    )}
                  />
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion
            disableGutters
            sx={{
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              mb: 2,
            }}
          >
            <AccordionSummary css={BottomLine} expandIcon={<ExpandMoreIcon />} sx={{ px: 0, mb: 2 }}>
              <div id='advanced-settings' css={[ScrollMargin, BarTitle]}>
                {t('advancedSettings', '高度な設定')}
              </div>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <div css={FormItem}>
                <label css={Label}></label>
                <div css={FormContent}>
                  <div css={[FlexBetween, BottomSpacing]}>
                    <div css={[Font16, InlineHelp]}>
                      {t('activityLevelSettings', '活動レベル設定')}
                      <Tooltip
                        title={
                          <span>
                            {t(
                              'dependingOnYourActivityPostureAndDeviceAttachmentTheDeviceMayFalselyDetectADecreaseInTheLevelOfUrinaryRetention',
                              '活動や姿勢、装着状況により尿のたまり具合の減少を誤検知する場合があります。',
                            )}
                            <br />
                            {t(
                              'selectingForPeopleWithLimitedMobilityMayReduceFalseDetections',
                              '「動きが少ない人用」を選択することで、誤検知を少なくできる場合があります。',
                            )}
                            <br />
                            {t('urinationRecordsAreNotTracked.', '※排尿記録はつきません')}
                          </span>
                        }
                        placement='top'
                        arrow
                      >
                        <HelpSharpIcon fontSize='small' sx={{ color: color.dark_gray }} />
                      </Tooltip>
                    </div>
                    <Controller
                      control={control}
                      name='detakamoLogic'
                      render={({ field: { onChange, value } }) => (
                        <>
                          <Select
                            listData={i18n.language.startsWith('ja') ? DETAKAMO_LOGIC_JA : DETAKAMO_LOGIC_EN}
                            value={value}
                            onChange={(v) => {
                              onChange(v.key);
                              onChangeDetakamoLogic(String(v.key));
                            }}
                          />
                        </>
                      )}
                    />
                  </div>

                  <div css={BottomSpacing}>
                    <Controller
                      control={control}
                      name='contingencyMode'
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          label={
                            <span css={InlineHelp}>
                              {t('rescueMode', '救済モード')}
                              <Tooltip
                                title={
                                  <span>
                                    {t(
                                      'pleaseTryThisModeIfYouHaveALotOfLowerAbdominalFatOrIfTheLevelOfUrinaryRetentionDoesNotIncreaseAboveLevel4',
                                      '下腹部の脂肪が厚い方への使用など、尿のたまり具合が4よりあがらない場合にお試しください。',
                                    )}
                                  </span>
                                }
                                placement='top'
                                arrow
                              >
                                <HelpSharpIcon fontSize='small' sx={{ color: color.dark_gray }} />
                              </Tooltip>
                            </span>
                          }
                          control={
                            <Checkbox
                              sx={{ padding: 0, marginRight: '8px' }}
                              checked={value}
                              onChange={(v) => onChange(v.target.checked)}
                            />
                          }
                        />
                      )}
                    />
                  </div>
                  <div css={ContingencyModeSub}>
                    {t(
                      'rescueModeMayMakeItEasierForTheLevelOfUrinaryRetentionToIncrease',
                      '救済モードは尿のたまり具合の数値をあがりやすくします。',
                    )}
                    <br />
                    {i18n.language.startsWith('ja') ? (
                      <>
                        ※ ご利用前に
                        <a href={SUPPORT_URL.howToWear.ja} target='_blank' rel='noreferrer'>
                          装着手順
                        </a>
                        を再度ご確認ください。
                      </>
                    ) : (
                      <>
                        * Please also refer to{' '}
                        <a href={SUPPORT_URL.howToWear.en} target='_blank' rel='noreferrer'>
                          the placement instructions
                        </a>{' '}
                        before using.
                      </>
                    )}
                    <br />
                    {t(
                      'theLevelOfUrinaryRetentionMayNotNecessarilyIncreaseAsAResultOfUsingTheseModes.',
                      '※必ず数値があがるわけではございません。',
                    )}
                    <br />
                    {t(
                      'thereIsAPossibilityThatTheLevelOfUrinaryRetentionMayIncreaseSuddenlySoPleaseBeAwareWhenUsingTheseModes.',
                      '※数値が急激にあがる可能性もありますので、ご利用には注意ください',
                    )}
                    <br />
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </ThemeProvider>
    );
  }
);
